import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Bell,
  Send,
  MessageSquare,
  Mail,
  Smartphone,
  AlarmClock,
  Trash2,
  CheckCheck,
} from 'lucide-react';
import useFetch from '../../hooks/useFetch';
import { post, del } from '../../api/client';
import { useEvents } from '../../context/EventContext';
import { useToast } from '../../context/ToastContext';
import EventPicker from '../../components/EventPicker';
import {
  Card,
  Button,
  Field,
  Input,
  Select,
  Textarea,
  Badge,
  Table,
  Loader,
  EmptyState,
  SectionTitle,
  Tabs,
  Pagination,
  ConfirmModal,
  label,
} from '../../components/UI';
import { fmtDateTime, fmtRelative } from '../../utils/format';

const CHANNELS = [
  ['in_app', 'In-app', Bell],
  ['push', 'Push', Smartphone],
  ['whatsapp', 'WhatsApp', MessageSquare],
  ['email', 'Email', Mail],
];

export default function Notifications() {
  const [tab, setTab] = useState('inbox');
  return (
    <>
      <SectionTitle
        title="Communication"
        subtitle="WhatsApp, email and push messaging across every participant"
        action={<EventPicker />}
      />
      <Tabs
        active={tab} onChange={setTab}
        tabs={[
          { key: 'inbox', label: 'My inbox', icon: Bell },
          { key: 'compose', label: 'Send a message', icon: Send },
          { key: 'sent', label: 'Delivery log', icon: CheckCheck },
          { key: 'scheduled', label: 'Scheduled alarms', icon: AlarmClock },
        ]}
      />
      {tab === 'inbox' && <Inbox />}
      {tab === 'compose' && <Compose />}
      {tab === 'sent' && <SentLog />}
      {tab === 'scheduled' && <Scheduled />}
    </>
  );
}

function Inbox() {
  const toast = useToast();
  const [page, setPage] = useState(1);
  const { data, meta, loading, reload } = useFetch('/notifications', { page, limit: 25 }, { withMeta: true });

  const markAll = async () => {
    try {
      await post('/notifications/read-all', {});
      toast.success('All notifications marked as read');
      reload();
    } catch (err) { toast.error(err.message); }
  };

  if (loading) return <Loader label="Loading your inbox" />;

  return (
    <>
      {meta?.unread > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 13 }}>
          <Button variant="ghost" size="sm" icon={CheckCheck} onClick={markAll}>
            Mark all as read ({meta.unread})
          </Button>
        </div>
      )}

      {!data?.length ? (
        <EmptyState message="Your inbox is empty" hint="Messages you receive about your events appear here." icon={Bell} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          {data.map((n, i) => (
            <motion.div key={n._id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.03, 0.3) }}>
              <Card padding={16} style={{
                borderLeft: `3px solid ${n.isRead ? 'var(--border)' : 'var(--gold-400)'}`,
                background: n.isRead ? 'var(--surface)' : 'rgba(239,212,138,0.035)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 13, alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
                      <h4 style={{ fontSize: 13.8, fontWeight: 600 }}>{n.title}</h4>
                      <Badge tone={n.priority === 'critical' ? 'danger' : n.priority === 'high' ? 'warning' : 'neutral'}>
                        {label(n.category)}
                      </Badge>
                    </div>
                    <p style={{ fontSize: 13, color: 'var(--text-mid)', marginTop: 6, lineHeight: 1.6 }}>{n.body}</p>
                    <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                      {n.channels?.map((c) => {
                        const entry = CHANNELS.find(([v]) => v === c);
                        if (!entry) return null;
                        const [, text, Icon] = entry;
                        const sent = n.delivery?.[c]?.sent;
                        return (
                          <span key={c} style={{
                            display: 'flex', alignItems: 'center', gap: 5, fontSize: 11,
                            color: sent === false ? 'var(--danger)' : 'var(--text-lo)',
                          }}>
                            <Icon size={11} /> {text}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  <span style={{ fontSize: 11.5, color: 'var(--text-lo)', whiteSpace: 'nowrap' }}>
                    {fmtRelative(n.createdAt)}
                  </span>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
      <Pagination meta={meta} onChange={setPage} />
    </>
  );
}

function Compose() {
  const { eventId, event } = useEvents();
  const toast = useToast();
  const [form, setForm] = useState({
    title: '', body: '', category: 'general', priority: 'normal', audience: 'all', scheduledAt: '',
  });
  const [channels, setChannels] = useState(['in_app', 'push']);
  const [busy, setBusy] = useState(false);
  const [schedule, setSchedule] = useState(false);

  const toggle = (c) => setChannels((cur) => cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c]);

  const send = async () => {
    if (!eventId) return toast.error('Select an event first');
    if (!form.title.trim() || !form.body.trim()) return toast.error('Add a title and a message');
    if (!channels.length) return toast.error('Pick at least one channel');
    if (schedule && !form.scheduledAt) return toast.error('Choose when the alarm should fire');

    setBusy(true);
    try {
      const endpoint = schedule ? '/notifications/alarm' : '/notifications/send';
      const res = await post(endpoint, {
        title: form.title, body: form.body,
        category: schedule ? 'alarm' : form.category,
        priority: form.priority,
        channels, event: eventId, audience: form.audience,
        ...(schedule ? { scheduledAt: form.scheduledAt } : {}),
      });
      toast.success(res.message);
      setForm({ title: '', body: '', category: 'general', priority: 'normal', audience: 'all', scheduledAt: '' });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.35fr) minmax(0, 1fr)', gap: 16, alignItems: 'start' }} className="gl-compose">
      <Card>
        <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 17 }}>Compose</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
          <Field label="Title" required>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Gala dinner starts at 8 PM" />
          </Field>
          <Field label="Message" required>
            <Textarea rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="Write your message…" />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Field label="Audience">
              <Select value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })}>
                <option value="all">All participants</option>
                <option value="vip">VIP guests</option>
                <option value="staff">Event staff</option>
              </Select>
            </Field>
            <Field label="Category">
              <Select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {['general', 'flight', 'hotel', 'bus', 'schedule', 'attendance', 'luggage'].map((c) => (
                  <option key={c} value={c}>{label(c)}</option>
                ))}
              </Select>
            </Field>
            <Field label="Priority">
              <Select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </Select>
            </Field>
          </div>

          <Field label="Channels" required>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {CHANNELS.map(([v, text, Icon]) => (
                <button key={v} type="button" onClick={() => toggle(v)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 7,
                    padding: '8px 15px', borderRadius: 99, fontSize: 12.5, fontWeight: 500,
                    border: `1px solid ${channels.includes(v) ? 'var(--gold-500)' : 'var(--border)'}`,
                    background: channels.includes(v) ? 'rgba(239,212,138,0.13)' : 'transparent',
                    color: channels.includes(v) ? 'var(--gold-300)' : 'var(--text-mid)',
                    transition: 'all 0.18s',
                  }}>
                  <Icon size={13} /> {text}
                </button>
              ))}
            </div>
          </Field>

          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13 }}>
            <input type="checkbox" checked={schedule} onChange={(e) => setSchedule(e.target.checked)}
              style={{ width: 15, height: 15, accentColor: 'var(--gold-400)' }} />
            <AlarmClock size={14} color="var(--gold-400)" />
            Schedule this as an alarm instead of sending now
          </label>

          {schedule && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}>
              <Field label="Send at" required>
                <Input type="datetime-local" value={form.scheduledAt}
                  onChange={(e) => setForm({ ...form, scheduledAt: e.target.value })} />
              </Field>
            </motion.div>
          )}

          <Button icon={schedule ? AlarmClock : Send} onClick={send} loading={busy} size="lg" style={{ marginTop: 5 }}>
            {schedule ? 'Schedule alarm' : 'Send now'}
          </Button>
        </div>
      </Card>

      {/* live preview */}
      <Card>
        <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>Preview</h3>
        <p style={{ fontSize: 12, color: 'var(--text-lo)', marginBottom: 17 }}>
          How it appears on an attendee's phone
        </p>

        <div style={{
          background: 'var(--ink-900)', border: '1px solid var(--border)',
          borderRadius: 'var(--r-xl)', padding: 15,
        }}>
          <div style={{ display: 'flex', gap: 11 }}>
            <div style={{
              width: 34, height: 34, borderRadius: 9, flexShrink: 0,
              background: 'linear-gradient(135deg, var(--gold-300), var(--gold-600))',
              display: 'grid', placeItems: 'center', fontSize: 15,
            }}>
              🪷
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--gold-400)' }}>GOLDEN LOTUS</span>
                <span style={{ fontSize: 10.5, color: 'var(--text-lo)' }}>now</span>
              </div>
              <p style={{ fontSize: 13, fontWeight: 600, marginTop: 4 }}>
                {form.title || 'Your notification title'}
              </p>
              <p style={{ fontSize: 12.3, color: 'var(--text-mid)', marginTop: 3, lineHeight: 1.55 }}>
                {form.body || 'Your message body will appear here as the attendee sees it.'}
              </p>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
          <p style={{ fontSize: 11, color: 'var(--text-lo)', textTransform: 'uppercase', letterSpacing: 0.9, fontWeight: 600, marginBottom: 10 }}>
            Delivering via
          </p>
          {channels.length ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {channels.map((c) => {
                const [, text, Icon] = CHANNELS.find(([v]) => v === c);
                return (
                  <div key={c} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12.5, color: 'var(--text-mid)' }}>
                    <Icon size={14} color="var(--gold-500)" /> {text}
                  </div>
                );
              })}
            </div>
          ) : <p style={{ fontSize: 12.5, color: 'var(--warn)' }}>No channel selected</p>}

          {event && (
            <p style={{ fontSize: 12, color: 'var(--text-lo)', marginTop: 15, lineHeight: 1.6 }}>
              Recipients: <strong style={{ color: 'var(--gold-400)' }}>
                {form.audience === 'vip' ? 'VIP guests' : form.audience === 'staff' ? 'Event staff' : 'All participants'}
              </strong> on {event.title}
            </p>
          )}
        </div>
      </Card>

      <style>{`@media (max-width: 980px) { .gl-compose { grid-template-columns: 1fr !important; } }`}</style>
    </div>
  );
}

function SentLog() {
  const { eventId } = useEvents();
  const [page, setPage] = useState(1);
  const { data, meta, loading } = useFetch(
    '/notifications/sent', { page, limit: 25, ...(eventId ? { event: eventId } : {}) }, { withMeta: true },
  );

  return (
    <>
      <Table
        loading={loading}
        rows={data}
        empty="No messages sent yet"
        columns={[
          { key: 'title', title: 'Message', render: (r) => (
            <div style={{ maxWidth: 300 }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{r.title}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-lo)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {r.body}
              </div>
            </div>
          )},
          { key: 'user', title: 'Recipient', render: (r) => r.user?.name || '—' },
          { key: 'category', title: 'Type', render: (r) => <Badge tone="neutral">{label(r.category)}</Badge> },
          { key: 'channels', title: 'Delivery', render: (r) => (
            <div style={{ display: 'flex', gap: 7 }}>
              {r.channels?.map((c) => {
                const entry = CHANNELS.find(([v]) => v === c);
                if (!entry) return null;
                const [, text, Icon] = entry;
                const d = r.delivery?.[c];
                const tone = d?.sent === false ? 'var(--danger)' : d?.simulated ? 'var(--warn)' : d?.sent ? 'var(--ok)' : 'var(--text-lo)';
                return <Icon key={c} size={13} color={tone} titleAccess={text} />;
              })}
            </div>
          )},
          { key: 'createdAt', title: 'Sent', render: (r) => (
            <span style={{ fontSize: 12, color: 'var(--text-lo)' }}>{fmtRelative(r.createdAt)}</span>
          )},
        ]}
      />
      <Pagination meta={meta} onChange={setPage} />
    </>
  );
}

function Scheduled() {
  const toast = useToast();
  const { eventId } = useEvents();
  const [cancelId, setCancelId] = useState(null);
  const { data, loading, reload } = useFetch(
    '/notifications/sent', { scheduled: 'true', limit: 50, ...(eventId ? { event: eventId } : {}) },
  );

  const cancel = async () => {
    try {
      await del(`/notifications/scheduled/${cancelId}`);
      toast.success('Scheduled alarm cancelled');
      setCancelId(null);
      reload();
    } catch (err) { toast.error(err.message); }
  };

  if (loading) return <Loader />;
  if (!data?.length) {
    return <EmptyState message="No alarms scheduled" hint="Schedule reminders for session starts, bus departures and flight timings." icon={AlarmClock} />;
  }

  return (
    <>
      <Table
        rows={data}
        empty="No scheduled alarms"
        columns={[
          { key: 'title', title: 'Alarm', render: (r) => (
            <div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{r.title}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-lo)' }}>{r.body}</div>
            </div>
          )},
          { key: 'user', title: 'Recipient', render: (r) => r.user?.name || '—' },
          { key: 'scheduledAt', title: 'Fires at', render: (r) => (
            <div>
              <div style={{ fontSize: 12.5 }}>{fmtDateTime(r.scheduledAt)}</div>
              <div style={{ fontSize: 11, color: 'var(--gold-500)' }}>{fmtRelative(r.scheduledAt)}</div>
            </div>
          )},
          { key: 'actions', title: '', align: 'right', width: 60, render: (r) => (
            <button onClick={() => setCancelId(r._id)} title="Cancel alarm"
              style={{ color: 'var(--danger)', padding: 6, display: 'flex' }}>
              <Trash2 size={15} />
            </button>
          )},
        ]}
      />
      <ConfirmModal
        open={Boolean(cancelId)} onClose={() => setCancelId(null)} onConfirm={cancel}
        title="Cancel this alarm?" danger confirmLabel="Cancel alarm"
        message="The recipient will not receive this reminder. This cannot be undone."
      />
    </>
  );
}
